import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, message, Form } from 'antd';
import styles from '../styles/reportDetail.module.less';
import Tab1Content from './components/Tab1Content';
import Tab2Content from './components/Tab2Content';
import ValidateModal from '../ValidateModal';
import { utils } from '@ohif/core';
import {
  normalizeDataIndex,
  formatStudyDate,
  getStateText,
  extractQueryParams,
} from '../../../../utils/reportHelper';

const { apiService } = utils;

export interface StudyData {
  register_key?: string;
  bodypart_name?: string;
  modality_code?: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_time?: string;
  check_view2?: string;
  diagnosis_result2?: string;
  yearMonthDay?: string;
  state?: string;
  stateText?: string;
  index?: number;
}

interface AuthState {
  isValidate: boolean;
  modalVisible: boolean;
  isSubmitting: boolean;
}

const ReportDetail: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<StudyData | null>(null);
  const [dataState, setDataState] = useState(false);
  const [studyInstanceUid, setStudyInstanceUid] = useState<string>('');
  const [studies, setStudies] = useState<StudyData[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [city, setCity] = useState<string | null>(null);
  const [province, setProvince] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const tokenManager = (window as any).tokenManager;

  // 路由参数解析
  const { params: initialParams, firstParamValue } = useMemo(
    () => extractQueryParams(location.search),
    [location.search]
  );

  const routeConfig = useMemo(
    () => ({
      hidename: queryParams.get('hidename') === 'true',
      registerKey: queryParams.get('register_key'),
      from: queryParams.get('from'),
      idSuffix: queryParams.get('id_suffix'),
      isShareMode: false,
      currentHost: `${window.location.protocol}//${window.location.host}`,
    }),
    [location, queryParams]
  );

  // 验证相关逻辑
  const storageKey = useMemo(() => {
    if (routeConfig.from === 'userreportportal' && routeConfig.idSuffix) {
      return `isValidate_${routeConfig.idSuffix}`;
    }
    return `isValidate_${firstParamValue}`;
  }, [firstParamValue, routeConfig.from, routeConfig.idSuffix]);

  const [authState, setAuthState] = useState<AuthState>({
    // isValidate: queryParams.get('view_type') == 'doc' ? true : !!localStorage.getItem(storageKey),
    isValidate: !!localStorage.getItem(storageKey) && tokenManager.getCurrentToken() !== null,
    modalVisible: false, // 初始值设为false，由useEffect控制显示
    isSubmitting: false,
  });

  const viewType = queryParams.get('view_type');

  useEffect(() => {
    if (viewType === 'doc') {
      const formData = new FormData();
      formData.append('view_type', 'doc');
      formData.append('register_key', queryParams.get('register_key'));

      apiService({
        uri: '/api/login/CloudVerify',
        method: 'POST',
        data: formData,
        type: null,
      }).then(res => {
        if (res.Status === '0' || res.Status === 0) {
          setAuthState(prev => ({ ...prev, isValidate: true }));
          const tokenManager = (window as any).tokenManager;
          if (tokenManager && tokenManager.setToken) {
            tokenManager.setToken(res.Token, res.ExpiresUtc, res.RefreshToken);
          }
        } else {
          messageApi.error(res?.Msg || '验证失败');
        }
      });
    }
  }, [viewType]);

  useEffect(() => {
    if (!authState.isValidate && viewType !== 'doc') {
      setAuthState(prev => ({ ...prev, modalVisible: true }));
    } else {
      setAuthState(prev => ({ ...prev, modalVisible: false }));
    }
  }, [authState.isValidate]);

  const isValidate = useMemo(() => authState.isValidate, [authState.isValidate]);

  // Ref定义
  const listLoadingRef = useRef<boolean>(listLoading);
  useEffect(() => {
    listLoadingRef.current = listLoading;
  }, [listLoading]);

  // 数据请求函数
  const fetchStudyList = useCallback(
    async (params: Record<string, any> = {}) => {
      if (listLoadingRef.current) return;

      const formData = new FormData();
      const coreParamKeys = [
        'study_id',
        'patient_no',
        'patient_id',
        'in_no',
        'out_no',
        'identification_no',
        'ybkh',
        'jzkh',
        'idCard',
        'register_key',
      ];

      let validParamCount = 0;
      Object.entries(params).forEach(([key, value]) => {
        if (coreParamKeys.includes(key) && value) {
          formData.append(key, value);
          validParamCount++;
        }
      });

      if (validParamCount === 0) return;

      formData.append('cloud_image', '1');
      setListLoading(true);

      try {
        const res = await apiService({
          uri: '/api/reglist/stream-page',
          method: 'POST',
          data: formData,
          type: null,
        });

        if (!res?.Data) throw new Error('检查列表数据为空');

        let formattedData = res.Data.map((item: any, index: number) => ({
          ...item,
          index: index + 1,
        }));

        formattedData = normalizeDataIndex(formattedData);

        formattedData.sort((a: StudyData, b: StudyData) => {
          const getTimeStamp = (item: StudyData) => {
            if (!item.study_time) return -Infinity;
            const time = new Date(item.study_time).getTime();
            return isNaN(time) ? -Infinity : time;
          };
          return getTimeStamp(b) - getTimeStamp(a);
        });

        formattedData = formattedData.map((item: StudyData) => ({
          ...item,
          yearMonthDay: formatStudyDate(item.study_time || '').yearMonthDay,
          stateText: getStateText(item.state),
        }));

        setStudies(formattedData);
      } catch (error) {
        setStudies([]);
        messageApi.error(
          `获取检查列表失败: ${error instanceof Error ? error.message : '未知错误'}`
        );
      } finally {
        setListLoading(false);
      }
    },
    [messageApi]
  );

  const getStudyInstanceUid = useCallback(async (registerKey: string) => {
    const formData = new FormData();
    formData.append('register_key', registerKey);

    try {
      const res = await apiService({
        uri: '/api/reglist/getSuid',
        method: 'POST',
        data: formData,
      });
      return res || '';
    } catch (err) {
      console.error('获取StudyInstanceUid失败:', err);
      throw new Error('无法获取检查胶片信息，请重试');
    }
  }, []);

  const fetchLocation = useCallback(async () => {
    const cachedLocation = localStorage.getItem('location');
    if (cachedLocation) {
      try {
        const { city, province } = JSON.parse(cachedLocation);
        if (city?.trim() && province?.trim()) {
          setCity(city);
          setProvince(province);
          return;
        }
      } catch (e) {
        console.warn('定位缓存解析失败，将重新请求:', e);
      }
    }

    try {
      const response = await fetch('/getaddress/ip/local/geo/v1/district');
      if (!response.ok) throw new Error(`HTTP请求失败: ${response.status}`);

      const resData = await response.json();
      if (!resData?.data || !resData.data.city || !resData.data.prov) {
        throw new Error('定位接口返回数据格式错误');
      }

      const locationData = {
        city: resData.data.city,
        province: resData.data.prov,
      };

      localStorage.setItem('location', JSON.stringify(locationData));
      setCity(locationData.city);
      setProvince(locationData.province);
    } catch (err) {
      console.error('获取定位信息失败:', err);
      setCity('');
      setProvince('');
    }
  }, [messageApi]);

  // 验证弹窗处理
  const handleValidateOK = useCallback(async () => {
    if (authState.isSubmitting) return;
    setAuthState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const { idCard } = await form.validateFields(['idCard']);
      const formData = new FormData();

      if (routeConfig.from === 'userreportportal') {
        const identificationNo = String(idCard ?? '')
          .trim()
          .toUpperCase();
        formData.append('identification_no', identificationNo);
      } else {
        formData.append('idCard', idCard);
        queryParams.forEach((value, key) => {
          formData.append(key, value);
        });
      }

      const res = await apiService({
        uri: '/api/login/CloudVerify',
        method: 'POST',
        data: formData,
        type: null,
      });

      if (res.Status === '0' || res.Status === 0) {
        setAuthState({
          isValidate: true,
          modalVisible: false,
          isSubmitting: false,
        });
        localStorage.setItem(storageKey, 'true');
        const tokenManager = (window as any).tokenManager;
        if (tokenManager && tokenManager.setToken) {
          tokenManager.setToken(res.Token, res.ExpiresUtc, res.RefreshToken);
        }

        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('isValidate_') && key !== storageKey) {
            localStorage.removeItem(key);
          }
        });

        messageApi.success('身份验证成功');
      } else if (res.Status === '1' || res.Status === 1) {
        setAuthState(prev => ({ ...prev, isSubmitting: false }));
        messageApi.error(
          routeConfig.from === 'userreportportal'
            ? '验证失败，请检查身份证号'
            : '验证失败，请检查手机号'
        );
      } else {
        setAuthState(prev => ({ ...prev, isSubmitting: false }));
        messageApi.error('验证响应异常，请重试');
      }
    } catch (error) {
      setAuthState(prev => ({ ...prev, isSubmitting: false }));
      messageApi.error(error instanceof Error ? error.message : '验证过程出错，请重试');
    }
  }, [authState.isSubmitting, form, queryParams, messageApi, storageKey, routeConfig.from]);

  // 页面跳转逻辑
  const handleGoHistory = useCallback(() => {
    const historyParams = new URLSearchParams(location.search);
    if (historyParams.get('from') === 'userreportportal') {
      navigate('/userreportportal', { state: { skipPortalAuth: true } });
      return;
    }
    ['register_key', 'flag', 'hidename', 'code', 'expiration', 'from'].forEach(key => {
      historyParams.delete(key);
    });

    const queryString = historyParams.toString();
    navigate(`/userreport${queryString ? `?${queryString}` : ''}`);
  }, [location.search, navigate]);

  // 数据加载逻辑
  useEffect(() => {
    // if ((authState.isValidate && !listLoadingRef.current)) {
    if (tokenManager.getCurrentToken()) {
      const params = Object.fromEntries(new URLSearchParams(location.search).entries());
      fetchStudyList({ ...params, param: firstParamValue });
    }
    // }
  }, [
    authState.isValidate,
    authState.modalVisible,
    firstParamValue,
    location.search,
    fetchStudyList,
  ]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  useEffect(() => {
    const fetchDetails = async () => {
      if (listLoading) return;

      setLoading(true);

      try {
        if (!tokenManager.getCurrentToken()) {
          return;
        }

        if (!routeConfig.registerKey) {
          messageApi.error('缺少必要参数，无法加载详情');
          return;
        }

        const matchedStudy = studies.find(item => item.register_key === routeConfig.registerKey);

        // if (!matchedStudy) {
        //   messageApi.error('未找到对应的检查数据');
        //   return;
        // }

        const uid = await getStudyInstanceUid(routeConfig.registerKey);

        setData(matchedStudy);
        setDataState(true);
        setStudyInstanceUid(uid);
        setLoading(false);
      } catch (error) {
        messageApi.error(error instanceof Error ? error.message : '加载详情失败，请重试');
        setLoading(false);
      }
    };

    fetchDetails();
  }, [
    studies,
    listLoading,
    routeConfig.registerKey,
    getStudyInstanceUid,
    messageApi,
    authState.modalVisible,
  ]);

  useEffect(() => {
    const onUnauthorized = () => {
      setStudies([]);
      if (viewType === 'doc') return;
      setAuthState(prev => ({
        ...prev,
        isValidate: false,
        modalVisible: true,
        isSubmitting: false,
      }));
    };
    window.addEventListener('token:unauthorized', onUnauthorized as EventListener);
    return () => {
      window.removeEventListener('token:unauthorized', onUnauthorized as EventListener);
    };
  }, [viewType]);

  // // 渲染逻辑
  // if (loading) {
  //   return <div style={{ color: "#fff" }}>加载中...</div>;
  // }

  // if (!data) {
  //   return (
  //     <div style={{ color: "#fff" }}>
  //       数据未加载，请检查链接或返回上一页重试
  //     </div>
  //   );
  // }

  return (
    <>
      {contextHolder}
      <div className={styles.containerWrap}>
        <h3 className={styles.title}>胶片详情</h3>
        <div className={styles.container}>
          {isValidate && !loading && (
            <Tabs
              type="card"
              defaultActiveKey="2"
              items={[
                {
                  key: '1',
                  label: '查看报告',
                  children: (
                    <Tab1Content
                      data={data}
                      hidename={routeConfig.hidename}
                      isShareMode={routeConfig.isShareMode}
                      currentHost={routeConfig.currentHost}
                      onShare={() => { }}
                      onDownload={() => { }}
                      onHistory={handleGoHistory}
                      studyInstanceUid={studyInstanceUid}
                    />
                  ),
                },
                {
                  key: '2',
                  label: '电子胶片',
                  children: (
                    <Tab2Content
                      studyInstanceUid={studyInstanceUid}
                      currentHost={routeConfig.currentHost}
                      city={city}
                      province={province}
                      modality_code={data?.modality_code}
                    />
                  ),
                },
              ]}
              className={styles.customTabs}
              destroyOnHidden={false}
            />
          )}
        </div>

        <ValidateModal
          visible={authState.modalVisible}
          isSubmitting={authState.isSubmitting}
          onOk={handleValidateOK}
          form={form}
          verificationMode={routeConfig.from === 'userreportportal' ? 'idCardFull' : 'phoneLast6'}
          title={'身份验证'}
        />
      </div>
    </>
  );
};

export default ReportDetail;
